import io from 'socket.io-client';
import { strapiClient } from './UniversalApiClient';

class ChatService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.listeners = new Map();
  }

  connect(token, userId) {
    // Derive Strapi base URL from the unified client to avoid hardcoded fallbacks
    const apiBase = strapiClient?.defaults?.baseURL || '';
    const strapiBase = apiBase.replace(/\/api$/, '');
    if (!strapiBase) {
      throw new Error('ChatService: Unable to determine Strapi base URL. Ensure REACT_APP_BACKEND_URL is set.');
    }

    const isLikelyJwt = (value) => {
      if (!value || typeof value !== 'string') return false;
      // Very small heuristic: three dot-separated base64url-ish segments
      const parts = value.split('.');
      return parts.length === 3 && parts.every(p => p.length > 10);
    };

    // Connect to Strapi with Socket.IO (cookie auth preferred)
    this.socket = io(strapiBase, {
      withCredentials: true,
      auth: isLikelyJwt(token) ? { token } : {},
      query: {
        userId: userId
      },
      transports: ['websocket'],
      upgrade: true
    });

    this.socket.on('connect', () => {
      console.log('Connected to chat server');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.emit('connection-status', { connected: true });
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Disconnected from chat server:', reason);
      this.isConnected = false;
      this.emit('connection-status', { connected: false, reason });
      
      if (reason === 'io server disconnect') {
        // Server disconnected, try to reconnect
        this.handleReconnect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('Connection error:', error);
      this.handleReconnect();
    });

    // Chat event listeners
    this.socket.on('new-message', (message) => {
      this.emit('new-message', message);
    });

    this.socket.on('user-joined', (user) => {
      this.emit('user-joined', user);
    });

    this.socket.on('user-left', (user) => {
      this.emit('user-left', user);
    });

    this.socket.on('online-users-count', (count) => {
      this.emit('online-users-count', count);
    });

    this.socket.on('typing-start', (data) => {
      this.emit('typing-start', data);
    });

    this.socket.on('typing-stop', (data) => {
      this.emit('typing-stop', data);
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
      
      setTimeout(() => {
        console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
        if (this.socket) {
          this.socket.connect();
        }
      }, delay);
    }
  }

  // Send message to chat
  sendMessage(message, roomId = 'public') {
    if (this.socket && this.isConnected) {
      this.socket.emit('send-message', {
        message,
        roomId,
        timestamp: new Date().toISOString()
      });
      return true;
    }
    return false;
  }

  // Join specific chat room
  joinRoom(roomId = 'public') {
    if (this.socket && this.isConnected) {
      this.socket.emit('join-room', { roomId });
    }
  }

  // Leave chat room
  leaveRoom(roomId = 'public') {
    if (this.socket && this.isConnected) {
      this.socket.emit('leave-room', { roomId });
    }
  }

  // Typing indicators
  startTyping(roomId = 'public') {
    if (this.socket && this.isConnected) {
      this.socket.emit('typing-start', { roomId });
    }
  }

  stopTyping(roomId = 'public') {
    if (this.socket && this.isConnected) {
      this.socket.emit('typing-stop', { roomId });
    }
  }

  // Event listener management
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => callback(data));
    }
  }

  // Get connection status
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts
    };
  }
}

// Singleton instance
const chatService = new ChatService();
export default chatService;